---
alert:
  anchorDrift:
    alarm: The ship has moved **{distance}** from the anchoring position.
    warning: The ship has moved **{distance}** from the anchoring position.
  depth:
    alarm: The current water depth is only **{depth}**.
    warning: The current water depth is only **{depth}**.
  lights:
    warning: The recommended lighting is **{recommended}**, but **{recorded}** is currently recorded.
  lostPosition:
    disconnected: The connection to the position source has been lost.
    invalidFix: The position source reports that its GPS fix is invalid.
    jump: The position source is reporting implausible positions. They are being ignored.
    stale: No position fixes have been received for some time.
  manOverboard:
    emitterActivated: A man-overboard emitter has been activated. Immediate all-hands response required.
    manualButtonActivated: A man-overboard emitter's manual button has been activated. Immediate all-hands response required.
    manual: A crew member has manually triggered a man-overboard alert. Immediate all-hands response required.
    unknown: A man-overboard signal has been received. Immediate all-hands response required.

anchorMonitor:
  explanation:
    fixedRadius: |-
      Alerts you when the ship drifts beyond a fixed distance from the anchor position, measured directly as a radius in meters.

      Use this method when you already know how much swing room you have — for example, in a crowded anchorage where you've judged the safe distance to neighbouring boats or the shore yourself.
    rodeScope: |-
      Calculates the expected swing radius from your anchor rode length, the water depth, and your boat's length, instead of a fixed distance you enter yourself.

      Use this method when you want LOGBOOK to work out a sensible radius for you based on how much rode you've let out — it adapts automatically as the tide changes the depth.
    sector: |-
      Watches a directional bearing sector instead of a full circle, so drift within an expected swing arc stays quiet while drift outside it triggers an alert.

      Use this method in a narrow channel or river anchorage, where swinging along the current is normal but drifting sideways towards a bank or another vessel is not.

changeLog:
  '2026-09-30': |-
    Version 1.1 adds many new useful features to LOGBOOK

    New Features
    * Manual ship status – to prevent unwanted “pause” entries (automatically enabled for "Bridge" and “Waiting” statuses)
    * Anchor watch with warning and alarm
    * Depth monitoring with warning and alarm
    * Notifications regarding visual signals required per COLREG
    * Manual engine entries: Notification of missing entry for “Engine On” (can be set retroactively)
    * Warning in case of problems with NMEA data
    * Man overboard alarm
    * History of changes – for even greater evidentiary reliability
    * Idle RPM for engines adjustable – idle operation is marked and does not count as travel under engine power

    Improvements
    * Even more functions for manual log entries now selectable directly in the Log menu
    * Manual engine entries: RPM can be added retroactively in Focus mode
    * Crew members, etc., can only be edited for the current day and the following day
    * Connecting Companions via Bonjour is possible for additional configurations
    * Improvements to the login dialog with more clear error messages
    * Extensive internal optimizations that make the app even faster and more reliable

    Bug Fixes
    * Many major and minor optimizations and bug fixes

  '2026-08-14': |-
    Here are the new features in the 2026-08-14 update:

    New Features
    * For completed days and days without log entries, entries can now be added retrospectively to the Weather Forecast, Journal, and Technical cards.
    * Support for internal GPS on Android
    * Warning when GPS fails
    * Quick selection of sails in the Log button menu
    * On Android devices, the Primary LOGBOOK now continues to run when the app is in the background
    * Display of sunrise and sunset times on the Day data sheet
    * New options Bridge and Waiting for stopovers and ship state
    * Separate speed setting for triggering the start entry at anchor and at buoy, to avoid unnecessary entries when swinging
    * Functions for uploading data to support

    Improvements
    * New design of NMEA and Sync buttons – current status more clearly visible
    * Log button menu now opens upwards to avoid unnecessary scrolling
    * GPS errors or excessive position deviations (e.g., caused by GPS jamming) are ignored
    * On small screens, the Stop button now only appears with an icon and can be expanded with the Expand button
    * When changing the reef stage of a sail, the previous reef stage is automatically removed
    * Improvements in the connection from Companion to Primary, support for Bonjour
    * Improved text for changes to crew, skipper, etc.
    * System-generated log entries (e.g., during data loss) now only appear once

    Bug Fixes
    * Time zone is now correctly detected on Windows even when the system is not set to English as the language
    * Possible errors in statistics that could occur with manual engine entries are automatically corrected with the update
    * System-generated duplicate log entries are automatically removed with the update
    * Android: images can now be uploaded
    * Many major and minor optimizations and bug fixes.

  '2026-07-15': |-
   Here are the new features in the 2026-07-15 update:
    
    New Features
    * For manual engine entries (where NMEA data does not include engine RPM), you can now enable a setting so that the manually entered RPM is automatically carried over to the next log entry. This is helpful during trips with a constant RPM. This option can be enabled in the **Ship settings** on the **Settings** tab.

    Improvements
    * Improvements and bug fixes to the statistics, particularly regarding the calculation of total time and total distance.
  
    Bug Fixes
    * Many major and minor optimizations and bug fixes.
  '2026-07-05': |-
    Here are the new features in the 2026-07-05 update:
    
    New Features
    * French is now available as an additional language for the user interface.
    * New, multifunctional, context-sensitive Log button next to the current log row for quickly adding various types of entries.
    * Images in notes (journal, etc.) can now be deleted.
    * An offset for the distance value from the log can be set in the ship settings (e.g., if the log does not display the correct total miles of the ship).

    Improvements
    * Various additional explanations and guidance in the user interface
    * Access rights and crew role of the account owner are now fixed to avoid problems with lost access rights.
    * When multiple additions are made to a log entry, all changes are now listed in the comment.
    * Extensive optimizations to the LOGBOOK Cloud and communication between LOGBOOK Cloud and LOGBOOK App

    Bug Fixes
    * When no engine data is available in NMEA, the engine buttons are now displayed immediately.
    * Fixed an issue where 500 rpm was entered for manual engine data after a data interruption.
    * Heading and wind are now correctly evaluated even when only relative data is present in NMEA.
    * Fixed an issue where negative times were displayed in statistics.

  '2026-06-11': |-
    The 2026-06-11 update brings many improvements and bug fixes:
    
    New Features
    * Support for iPhone as a companion device.
    * Evaluation of additional NMEA data sets for heading, wind, air temperature, and air pressure.

    Improvements
    * When multiple additions are made to an entry (e.g., engine off and sails set), they are now fully listed in the comment.
    * Logging out of the Primary LOGBOOK is now only possible together with closing the Primary LOGBOOK.
    * Optimized user guidance through improved hints, recommendations, and warnings.

    Bug Fixes
    * NMEA test now correctly detects when there are values from the log.
    * Fixed an issue where the wrong day status was displayed on the day card.
    * Fixed an issue where incorrect crew changes were logged after the device went into sleep mode.
    * Fixed an issue where an incorrect engine speed was recorded after an NMEA connection interruption.
    * Further optimizations and bug fixes in the statistics.

  '2026-05-30': |-
    Here are the new features in the 2026-05-30 update:
    
    New Features
    * LOGBOOK can now be used on board with multiple devices simultaneously. In this first stage, the login is done on all devices with the same account. See the documentation at <a href="https://logbook.yachts/site/en/documentation/#h1_devices" target="_blank">**Multiple Devices**</a>.

    Bug Fixes
    * Fixed an issue where images occasionally extended beyond the print area when printing.
    * Fixed an issue where the wrong date was displayed in western time zones.
    * The current day now always shows the correct date and no longer shows January 1st, even if there are no entries.
    * Changes in the next day's data sheet are not logged.
    * Fixed an issue where changes in the crew were logged even though the crew was not changed.
    * Fixed an issue where manual engine entries after an NMEA connection interruption recorded an engine speed.
    * When stopping, the correct duration now appears directly on the stop button.
    * Fixed an issue where the distance and duration were counted twice during the day's statistic.
    * iPad: Fixed a white screen when switching from Cloud to App within the app.

  '2026-05-20': |-
    Here are the new features in the 2026-05-20 update:
    
    New Features
    * Added NMEA connection via UDP.
    * Track data is continuously calculated and regularly synchronized, not just at the end of the day.
    * For days when there is no track, the missing track is automatically generated when recalculating the statistics (button on the map statistics).
    * For manual engine entries, the stop dialog offers an option to automatically create entries for **Engine off** as well.

    Improvements
    * Damping in the detection of pauses. This should prevent false pauses at low speeds or due to GPS inaccuracies, as well as incorrect endings of pauses during short-term ship movements or GPS errors.
    * The **calculation of total time** starts when the ship departs and ends with the last entry when the ship is no longer moving. Entries not related to the trip at the beginning and end are no longer considered.

    Bug Fixes
    * Fixed an issue where after restarting navigation on the same day, the distance started from 0 again.

  '2026-05-12': |-
    Here are the new features in the 2026-05-12 update:
    
    New Features
    * iPad: The LOGBOOK app continues to run in the background and continues to create log entries when the app is not in the foreground.
    * iPad: Photos can now be taken directly from the app and inserted into the logbook entries.
    * iPad: Support for **external GPS devices** connected via **Bluetooth** or cable
    * New **Reset** function in case the connection of the cloud to the Primary LOGBOOK was accidentally disconnected (indicated by the red marker on the sync button)

    Improvements
    * iPad: Layout issues after returning from the background fixed
    * Server: Improvements in the calculation of statistics

  '2026-05-05': |-
    The update 2026-05-05 fixes a few issues:
    
    * Fixed an issue with the integrated GPS of the iPad, which could lead to incorrect pause entries at very low speeds.
    * Fixed an issue where a disabled NMEA connection was automatically activated when the app was brought to the foreground on the iPad.
    * Fixed an issue where log entries and the log row were not displayed correctly when changing the window size (e.g., by rotating the tablet).
    * Fixed incorrect field format in the ship data on the **Equipment** tab.
    * Various minor layout optimizations
    * Various minor bug fixes

    If you are using the internal GPS for data reception, you can find important information in our tip <a href="https://logbook.yachts/site/en/support/#ipadgps" target="_blank">**»Using LOGBOOK with the internal GPS of the iPad«**</a>.
    
  '2026-04-27': |-
    The update 2026-04-27 brings a whole range of improvements and bug fixes. 
    This includes further optimizations of the layout for different screen sizes as well as error handling when importing from Logbook Suite.
    
  '2026-04-14': |-
    Here's the first, smaller update. Here's what we've improved:

    * Optimized layout for medium screens such as tablets in portrait mode (768px to 1023px)
    * Print button now works on iPad
    * Improvements when importing from Logbook Suite
    * Various minor bug fixes
    
  '2026-04-04': |-
    Welcome to LOGBOOK – the innovative logbook software for sailors and motorboat enthusiasts.

    This is version 1.0. In the future, you will find information about new features and improvements here after each update.

connection: 
  interrupted: |-
    The connection to the application was interrupted. 
    
    If the connection does not recover automatically, please try restarting the App.
  recovered: |-
    The connection to the application was restored. 
    
    Please reload to continue using the App.

currentView:
  firstTrip: |-
    **A new passage is being started** 
    
    Please check the **timezone** that is set for this passage. 
    
    The timezone is automatically set to the system timezone of the Primary Device. To change the timezone, you must change the system timezone of the Primary Device **before** starting the passage. 
  
    If the Primary Device system has not correctly transmitted the timezone to LOGBOOK, you can manually change the timezone in the **Ship Settings** on the **Settings** tab. This must also be done **before** starting the passage.
    
    **Please note:** Once the passage is started here, the timezone is fixed.

  demoWillStart: |-
    **Note:** With the start of the first passage, you also start your 5-day trial phase.
    
  confirmationDialog:
    cruise: 
      allowed: |-
        Starting a new voyage terminates the current voyage.

        Are you sure you want to stop and terminate the current voyage?

        **Please note**: NMEA recording will be stopped.
      deniedInMotion: |-
        Starting a new voyage is not possible because the ship is in motion.
      deniedBlankExists: |-
        Starting a new voyage is not possible because there is already an empty voyage that has not yet been started.
    trip: 
      allowed: |-
        Starting a new passage terminates the current passage.
        
        Are you sure you want to stop and terminate the current passage?

        **Please note**: NMEA recording will be stopped.
      deniedInMotion: |-
        Starting a new passage is not possible because the ship is in motion.
      deniedBlankExists: |-
        Starting a new passage is not possible because there is already an empty passage that has not yet been started.

importShip: 
  text: |-
    How to import your logbooks from **Logbook Suite** into **LOGBOOK**:

    **Step 1:** In Logbook Suite, use the **Export for LOGBOOK** function (available in Logbook Suite Manager version 2.6 and later) via the **More …** button to export all logbooks you created for your ship.

    **Step 2:** In LOGBOOK, create a new ship with the same name. Close the dialog displayed after creation to view your account settings in the LOGBOOK Cloud. (Skip this step if you've already created the ship. Make sure the ship is closed in both the Cloud and the app before proceeding to Step 3.)

    **Step 3:** Your new ship now appears in the list. Select **Options** for the ship you want to import into, then choose **Import from LogbookSuite**. Follow the instructions. (The import function is only available when the ship's LOGBOOK is not open.)

    **Step 4:** In the LOGBOOK app, start a new voyage before beginning or continuing data recording with LOGBOOK.
        
importTrips: 
  text: |-
    Import logbooks created with **Logbook Suite** for your ship **{name}** into **LOGBOOK**. Select the file created by the Logbook Suite export tool with the following name: ***[Ship Name]_LOGBOOK_export.logbook***

    If your imported logbook contains ship data not yet recorded in LOGBOOK, the data from Logbook Suite will be imported. Ship data already entered in LOGBOOK will not be overwritten.

    **Important Note:** The import process may take some time, especially if you have a large logbook. Please be patient and do not interrupt the process.

    **Attention:** After import, you must start a **new voyage** before starting or continuing data recording in LOGBOOK.

login: 
  welcomeCloud: |-
    Log in or create your account and start keeping your ship's logbook with LOGBOOK. 
    
    You can try LOGBOOK for free and create entries for 5 days.

  welcomeApp: |-
    Login to the LOGBOOK app.
    
    If you don't have an account yet, you can create one [here](logbook:signup).

  welcomeAppiPad: |-
    Login to the **LOGBOOK for Yachts** app for iOS.

    If you don't have an account yet, you can create one [here](logbook:signup).

  signupSuccess: |-
    Your account was created.

    We sent an email to your account for activation. Please follow the link in the Email to proceed.

  invitation: |- 
    {inviter} has invited you and would like to give you access to his LOGBOOK for **{ship}**. 
    
    If you already have an account for LOGBOOK, just log in to your account. 
    
    If you are not yet registered with LOGBOOK, please sign up here to create your free account. You will then be logged in directly.
    
    After logging in, your LOGBOOK account will be connected to {inviter}'s LOGBOOK account. Then, log in to the LOGBOOK app. In the ship list in your account settings, **{ship}** will appear under **Select from shared ships**, and you can directly connect as a companion to the LOGBOOK for {ship}.

  invitationLoggedIn: |- 
    {inviter} has invited you and would like to give you access to his LOGBOOK for **{ship}**. 
    
    By **Accept invitation**, you confirm the invitation. Then, log in to the LOGBOOK app. In the ship list in your account settings, **{ship}** will appear under **Select from shared ships**, and you can directly connect as a companion to the LOGBOOK for {ship}.

    By **Use other account**, you can also select or create another account to gain access to the LOGBOOK for {ship}.

  resetAppWarning: |-
    If you have problems starting the app or opening a ship, you can reset the LOGBOOK app with this function.

    All data stored on the device will be deleted. All data that has been uploaded from the LOGBOOK app to the LOGBOOK Cloud and stored there will be retained.
  
    After resetting, you must reopen the LOGBOOK app and log in with your account. When opening your ship, the LOGBOOK app will then load all data from the LOGBOOK Cloud again.
    
    **Important!** Only perform the LOGBOOK app reset when you have an internet connection.
    
    Are you sure you want to reset the app?

  resetAppComplete: |-
    The LOGBOOK app reset is complete.
    
    If the LOGBOOK app was not automatically closed, you must manually close (kill) it through the operating system and restart it.

logout: 
  logoutWithInternet: |-
    **Warning – Primary LOGBOOK for {name} open**
    
    The **Primary LOGBOOK {name}** is still open in the app. Logging out will close the Primary LOGBOOK. To reopen the Primary LOGBOOK, an internet connection is required.   

  logoutWithoutInternet: |-
    **Logout not possible – no internet connection** 
    
    An internet connection is required to log out. Please check your connection and try again.
    
nmeaSettings:
  connectionTypeNative: |-
    To use the **internal GPS**, select the **Built-in GPS** option under **NMEA connection type**. On the **Test and Data** card, GPS reception via the built-in GPS is displayed separately.

    An **external GPS device** connected via **Bluetooth** or **cable** to the iPad behaves the same way as the internal GPS. Therefore, for the external GPS device, also select the **Built-in GPS** connection type.
    
    Additional available **internal sensors**, such as an **internal barometer**, can be activated independently of the built-in GPS on the **Internal Sensors** card.

  connectionTypeNativeWarning: |-
    **Important note**
    
    To avoid incorrect entries when using the internal GPS, before taking the iPad off the ship, either disconnect the NMEA connection or close the Primary LOGBOOK of your ship via the menu command.

    Please note that an internet connection is required both for closing the ship and for reopening it.

noteText:  
  uninitialized: |-
    **Synchronization initializing**

    Please wait. This can take a few minutes.
  failed: Synchronization failed. Retrying soon.
  initialSyncFailed: Initial synchronization failed. Retrying soon.
  initialSyncInProgress: |-
    **Initial synchronization started**
    
    Please wait. This can take a few minutes.
  dataWaiting: Waiting to synchronize.
  inProgress: |-
    **Synchronization in progress** 
    
    Please wait. This can take a few minutes.
  current: Fully synchronized.
  disconnected: |-
    **Connection to the LOGBOOK Cloud lost**

    This device is no longer registered as the Primary LOGBOOK, so synchronization has stopped.

    Use **Reconnect** in the **Sync** menu to restore the connection. You will be asked whether the data on this device or the data in the LOGBOOK Cloud should be kept.
  transition: |-
    **Preparing your data**

    Existing entries are being updated for this version. Please wait, this can take a few minutes.
  resync: |-
    Data from your device is being uploaded to the LOGBOOK Cloud.
  newShip: |-
    **Important note** 
    
    The **vessel type** and the **number of engines** can not be changed after the ship has been created.

  shipOpen: |-
    The **Primary LOGBOOK** for **{name}** is already open on another device.
    
    **Case 1 - You are on board the ship**

    Make sure your device is connected to the **ship's internal network**.
    Then enter the address of the Primary LOGBOOK here to establish a connection. Use the format **IP:Port**, e.g., 192.168.0.100:8000. You can find the address in the Primary LOGBOOK in the **Ship Settings** under **Companions**.

  shipOpen2: |-
    ** **

    **Case 2 - No ship's internal network available or you are not on board the ship**
  
    You can always view the last synchronized state of the logbook in the **LOGBOOK Cloud**.

    ** **

    **Case 3 - You want to open the logbook as Primary LOGBOOK on this device**

    If necessary, you can use **Disconnect Primary LOGBOOK** to forcibly disconnect the connection between the Primary LOGBOOK and the LOGBOOK Cloud. 
  
  shipNotClosed: |-
    Use this function only if you are sure that the Primary LOGBOOK must be disconnected from the LOGBOOK Cloud – for example, because the Primary Device is defective or for other reasons access to the Primary Device is no longer possible.
    
    All data recorded after disconnecting from the Primary LOGBOOK will not be synchronized and may be lost.
        
    - Use the button below to switch to the ship list in the LOGBOOK Cloud.
    
    - Open the **Options** dialog with the **Options** button (the button shows a different color for the affected ship). You can now disconnect the ship via this dialog.

    After a short wait, the Primary LOGBOOK of the ship can be reopened. If necessary, you can refresh the view with the refresh button. Reopening can take several minutes as the LOGBOOK app must retrieve all data from the LOGBOOK Cloud.

  shipOpenCompanion: |-
    Enter the address of the Primary LOGBOOK here to establish a connection. 
    
    Use the format **IP:Port**, e.g., **192.168.0.100:8000**. 
    
    You can find the address in the Primary LOGBOOK in the ship settings on the **Companions** card.
 
  shipsSmartphone: |-
    LOGBOOK can only be used as a Companion on a smartphone and therefore offers limited functionality.

    Only ships whose Primary LOGBOOK is already running on another device can be selected. For the Companion connection, your smartphone must be on the same network as the Primary Device.

  selectShip: |-
    Selecting ship on LOGBOOK cloud.
  selectShipFailed: |-
    Selection of ship failed on LOGBOOK cloud.

    Please try again in a few minutes.
  limitActiveShipsReached: |-
    You have reached your limit of active ships. 

    To select another ship you must first close the currently open ship on LOGBOOK cloud.
  startShip: |-
    <strong>Your ship "{name}" has been created – Now switch to LOGBOOK app</strong>
    
    Your new ship has been successfully created and is now displayed here in the **LOGBOOK cloud**. 
    
    If you want, you can directly enter more ship data here in the LOGBOOK cloud. To do this, select the menu item **{name} Ship Data** from the menu <strong>{name}</strong>. 

    Next, the first voyage and the first passage need to be prepared. To do this, open your new ship in the **LOGBOOK app**. If you have not yet installed the LOGBOOK app, you can find the download links here: <a href="https://logbook.yachts/site/en/download" target="_blank">Download LOGBOOK app</a>.  

    Once the first voyage and the first passage have been created, they will also be displayed here in the LOGBOOK cloud. 
  firstLogin: |-
    **Welcome to LOGBOOK** – the innovative logbook software 
    
    **Your account has been successfully created.**
    
    <li>Now create your <strong>first ship</strong> …</li>
    <li>… set up the <strong>NMEA connection</strong> …</li>
    <li>… and you're <strong>ready for your first passage</strong> with LOGBOOK.</li>

    Detailed instructions can be found in the <a href="https://logbook.yachts/site/en/documentation/#h1_quickstart" target="_blank">QuickStart</a> and in the <a href="https://logbook.yachts/site/en/demos" target="_blank">Interactive Demos</a>.

  firstLoginCloud: |-
    If you have not yet installed the LOGBOOK app, you can find the download links here: <a href="https://logbook.yachts/site/en/download" target="_blank">Download LOGBOOK app</a>.  

sessionExpired:
  text: |-
    **Login required – no connection to the LOGBOOK Cloud**

    The LOGBOOK Cloud no longer accepts this device's login. Nothing has been lost: your ship stays open and everything you log is saved on this device.

    Until you log in again, no data is synced to the Cloud and no other device sees your entries.

shipClose:
  text: |-
    Are your sure you want to close the Primary LOGBOOK for the ship named **{name}**?

    **This will stop data recording and disconnect from NMEA.**

    Please also note: You will need an **Internet connection** if you want to get back to this ship later!

  internet: |-
    **Closing the ship is not possible – no Internet connection**

    Internet connection is needed to close the ship. Please check your connection and try again.

  forceWarning: |-
    **Force close only on this device?**

    This closes **{name}** locally without synchronizing. The ship remains claimed in the LOGBOOK Cloud and must be closed manually there before it can be opened again.

    Unsynced local data remains stored on this device.

  keepOpen: |-
    **Tip:**
    <p>We recommend keeping the <strong>Primary LOGBOOK open</strong> throughout the entire voyage. <br>
    After the end of the day, you can simply close the app. <br>
    Keep the <strong>Primary LOGBOOK open</strong> and <strong>do not log out</strong>. The data recording will then be interrupted, but the status of the Primary LOGBOOK will be retained. <br>
    After restarting the app, the Primary LOGBOOK is immediately available.</p>

  noSync: |-
    Synchronization with the LOGBOOK Cloud has failed because this LOGBOOK is no longer registered as the Primary LOGBOOK in the LOGBOOK Cloud.
    
    If you close the ship, all changes that have not yet been saved to the LOGBOOK Cloud will be lost.

    You can try to restore the connection to the LOGBOOK Cloud using **Reconnect** from the **Sync** menu, synchronize the data, and then close the ship.

shipInfo:
  disconnect: |-
    Are your sure you want to disconnect the Primary LOGBOOK for the ship named **{name}**?

    **This will stop any synchronization of this logbook  with the LOGBOOK Cloud.**
    
    Please also note:
    
    In case that the Primary LOGBOOK on the ship doesn't have Internet connection, all data that are recorded after disconnecting will not be synchronized and may be lost.
  resync: |-
    The Primary LOGBOOK for your ship **{name}** is no longer connected to the LOGBOOK Cloud. To restore this connection, the data must be synchronized again.
  
    Either the data on the device or the data in the LOGBOOK Cloud will be reset. Then this device will be set up as the **Primary LOGBOOK** for **{name}**. Finally, the data will be synchronized between the Primary LOGBOOK and the LOGBOOK Cloud.

    **Warning!**
    
    This function is intended for emergency use only. Only execute it on the device that is set up as the **Primary LOGBOOK** for **{name}**. If the Primary LOGBOOK is actually set up on a different device, the data recorded with that device may be lost.
    
    * First, check whether the data here in the LOGBOOK app or in the LOGBOOK Cloud is more recent.

    * Then decide whether you want to keep the data here in the LOGBOOK app or the data in the LOGBOOK Cloud as the current data.

    * Select the appropriate button to start the process.

  resyncUnavailable: |-
    The reset of the LOGBOOK for your ship **{name}** could not be completed because a device is still connected to the ship in the LOGBOOK Cloud.

    **Important:** First, check whether the Primary LOGBOOK is currently running on another device. If so, start the process from that other device and follow the steps there.

    To proceed here, disconnect this device in the LOGBOOK Cloud:

    * Switch to the LOGBOOK Cloud using the **To LOGBOOK Cloud** command from the **Account** menu.
    * In the LOGBOOK Cloud, the **Options** button next to your ship is highlighted in blue.
    * In the options, select **Disconnect Ship**.
    * Switch back to the LOGBOOK app.
    * Open the dialog to restore synchronization again. Now you can choose which data you want to keep.
    
  resync_reset_local_confirm: |-
    **Do you really want to use local data?**
    
    This action will reset the cloud data.

    This action cannot be undone and will lead to the loss of all data that is stored in the LOGBOOK Cloud and not present on this device.
  resync_reset_cloud_confirm: |-
    **Do you really want to use cloud data?**
    
    This action will reset the local data.

    This action cannot be undone and will lead to the loss of all data that is stored only on this device and not present in the LOGBOOK Cloud.
shipSetting:  
  utc: |-
    **Important note**   
    
    **Please note that the timezone for the ship's time is fixed for the entire passage once it has started.** Any changes here will not affect an already started passage.

  utc_info: |-
    LOGBOOK generally sets the ship's time to the system time of the Primary Device. 
    
    In some cases, the system of the Primary Device may not correctly transmit the timezone to LOGBOOK. In this case, you have the option to manually set the timezone for the ship's time.
    
  log_offset: |-
    If your log transducer has been replaced and the previous log value could not be transferred, enter the old value here as an offset. It will be automatically added to the new measurements, ensuring that the total distance of your ship is correctly continued.
  keep_rpm: |-
    If there is no engine data available via NMEA, you can enable this option to automatically carry over the RPM value you entered manually to the next log entry. This is useful when the engine speed does not change between entries.
  lights: |-
    When enabled, LOGBOOK quietly reminds you in a button on the current log row whenever the visual signals recorded for the ship do not match what COLREGS recommends for the current propulsion state and time of day. 
    
    It never sounds an alarm — recording the matching visual signals dismisses the reminder. 
    
    Configure which lights your ship carries when sailing, whether the day/night boundary follows civil twilight or sunset/sunrise, and whether the optional motoring cone should be reminded about.
  edit_permissiveness: |-
    Controls how permissive editing a log or day entry is once it has been recorded. This affects every existing and future entry as soon as it is changed — it is not limited to entries created afterwards.

    **Default (strict)** — today's behavior. A value that was supplied live by NMEA (e.g. heading, depth, engine RPM, log, pressure, temperature, humidity, wind, course, speed over ground) stays locked until you manually correct it, and can only be edited within a short time after it was recorded.

    Manual engine RPM is an exception to the time restriction: it can be entered or corrected on earlier entries too. RPM that is still protected as NMEA-supplied remains locked.

    **Extended** — lifts the time restriction, so NMEA-supplied values can still be corrected later, but values that haven't been manually corrected yet remain protected.

    **Unrestricted** — removes all of this protection. Every value, including ones supplied live by NMEA, can be edited at any time. **Use this with care:** it removes the safeguard that shows a value was recorded automatically from a sensor rather than entered or changed by hand, which can matter for the evidentiary value of your logbook.

    A handful of fields (e.g. distance) are always calculated and can never be edited directly, at any level.
  
planDescriptions:
  captain: |-
    Annual subscription for professionals, ship length under 24 m
  master_captain: |-
    Annual subscription for professionals, ship length from 24 m
  skipper: |-
    Monthly or annual subscription for private skippers
  skipper_inApp: |-
    Monthly subscription for private skippers – in-app purchase
  trial: |-
    5 Days Free Trial

subscription:
  checkoutComplete: |-
    Purchase successful.

  checkoutProcessing: |-
    Completing your purchase...

  subscriptionMessage: |-
    Pick a plan.

    No matter which device you purchase this subscription from, you can use LOGBOOK with all supported devices simultaneously or alternately (macOS, Windows, Android, and iOS). The subscription is not tied to a specific platform.

  subscriptionMessageiPad: |-
    Subscribe to keep using LOGBOOK with full functionality after your trial ends.

    Once the 5 free days are over, you can only view and print your saved data — no further entries or edits are possible.

    The monthly subscription **LOGBOOK Skipper** is particularly suitable for private skippers who prefer or exclusively want to keep their logbook on the iPad. The subscription renews automatically and can be canceled at the end of each term.

    This subscription may also be used on other platforms, simultaneously or alternately.

  demo1Day: |-
    **Trial phase ends in one day**

    You still have one day to test LOGBOOK for free and create entries.

    After that, you can seamlessly continue with a subscription and keep your logbook going without interruption.

  demo2Days: |-
    **Trial phase ends in 2 days**

    You can still create entries in LOGBOOK for free for 2 more days.
    
    After that, you can seamlessly continue with a subscription and keep your logbook going without interruption.
    
  demoEnded: |-
    **Trial phase ended**

    Your demo phase is over. With a subscription, you can now continue your logbook. We'll keep your test data stored in the LOGBOOK Cloud for another 6 months.

  demoToday: |-
    **Trial phase ends today**

    You can still create entries in LOGBOOK for free today.
    
    After that, the trial phase ends. With a subscription, you can continue your logbook at any time.
  
  demoStarted: |-
    **Trial phase started**

    Your trial phase started with your first entry in LOGBOOK. 
    
    You can now create logbook entries for a total of **5 days**.

  subscription7Days: |-
    **Subscription ends in a few days**
    
    Your subscription will expire soon. Don't worry – the renewal will be automatic, so you can continue using your logbook without interruption.

    If you want to adjust your subscription, you can do so at any time.

  subscriptionExpired: |-
    **Subscription expired**

    Your subscription has expired. But don't worry – all your data will remain securely stored in the LOGBOOK Cloud for another 2 years.

    Renew your subscription now to continue your logbook.

  subscriptionToday: |-
    **Subscription ends today**

    Your subscription expires today. Renew it now to continue using your logbook.

  removeAccount: |-
    Confirm account removal.

    This will permanently delete the "{account}" account and the "{user}" user.

    You will lose access to all ships, voyages, passages, and logbook entries associated with this account.

  removeAccountDone: |-
    Your account "{account}" and the user "{user}" in LOGBOOK has been successfully deleted.

mailer:
  footer: |-
    ---

    ![TRIMUTIC – THE LOGBOOK COMPANY](https://trimutic.com/media/trimutic_komplett-black.png "logo")

    [TRIMUTIC](https://trimutic.com) | [LOGBOOK](https://logbook.yachts) | [Privacy](https://logbook.yachts/site/en/privacy) | [About us](https://logbook.yachts/site/en/about)

    {reason}
  templates:
    signupConfirmation:
      subject: "Welcome to LOGBOOK - Confirm your email"
      footer: |-
        This email was sent because you created an account with LOGBOOK on our website.
        If you didn't create an account with LOGBOOK, you can safely ignore this email.
      body: |-
        Hello {name},

        Welcome to LOGBOOK - Confirm your email

        To get started, please confirm your email address by clicking the button below:

        [Confirm your Email Address]({confirmation_link} "button")

        This helps us ensure your account is secure and that you can receive important updates about your logbook.
        If the button above doesn't work, copy and paste this link into your browser:

        {confirmation_link}

        This confirmation link will expire in 24 hours.
        If you didn't create an account with LOGBOOK, you can safely ignore this email.

        Best regards,
        **The LOGBOOK Team**
    signupWelcome:
      subject: "Welcome to LOGBOOK"
      footer: |-
        You're receiving this email because you signed up for LOGBOOK.
      body: |-
        Hello {name},

        Welcome to LOGBOOK!

        Your email address has been successfully confirmed! 🎉\
        Your account is now activated and ready to use.

        **What's next?**

        Get started right away with your digital logbook!\
        With the free **LOGBOOK Trial**, you can create logbook entries for free on a total of **5 days**. The 5 days start with your first log entry.

        **Here's how to get started:**

        1. **Download the LOGBOOK app** and log in with your credentials.

           [Download app](https://logbook.yachts/site/en/download "button")

        2. **Create your first ship** and your first logbook entry – completely free.

        3. **Find support to get started:**

           - Step-by-step guide in the [Quickstart](https://logbook.yachts/site/en/docs/quickstart/)
           - Explore playfully with our [interactive demos](https://logbook.yachts/site/en/demos/)
           - Complete overview in the [Documentation](https://logbook.yachts/site/en/docs)

        4. **Tips & Help:**

           - Practical tricks on our [Support page](https://logbook.yachts/site/en/docs/support/#tips).
           - Direct help via the **Help** menu in the app or by email at contact@trimutic.com – we'll respond as quickly as possible!

        ---

        **Do you like LOGBOOK?**\
        Then secure a subscription now and keep your logbook permanently and without interruption.

        [More info about subscriptions](https://logbook.yachts/site/en/prices "button")

        [Subscribe now](https://logbook.yachts/app/account/subscription "button")

        **Note:** Unfortunately, on iOS it's not possible to select the subscription directly from your account settings in the app. On the iPad, you need to log into your account in a web browser (e.g., Safari) – outside the app. The **Subscribe now** button will then take you to the subscription selection.

        If you have any questions or problems, our [Support Team](https://logbook.yachts/site/en/support) is happy to help.

        Best regards,\
        **The LOGBOOK Team**
    invited:
      subject: "Welcome to LOGBOOK"
      footer: |-
        This email was sent because you have been invited to join LOGBOOK.
        If you do not wish to accept the invitation, you can simply ignore this email.
      body: |-
        Hello {name},

        {account_holder} has invited you to LOGBOOK so you can access the logbook aboard the **{ship_name}**.

        Click this button to accept the invitation:

        [Accept invitation]({invitation_link} "button")

        This button will take you to the LOGBOOK signup and login page.

        **Either log in …**
        If you already have a LOGBOOK account, you can log in using it. Your name email address {email} are already filled in. However, you can also log in using a different email address.

        **… or sign up and create an account**
        If you don’t have an account at LOGBOOK yet, you can sign up for LOGBOOK for free.
        All you need is your name, email address, and a password.
        Your email address {email} is already pre-filled so you can sign up without having to verify it again.
        Enter your name, set a password and then click on **Sign Up**.

        If the button above doesn't work, copy the following link and paste it into your browser:

        {invitation_link}

        This invitation link expires in 24 hours.

        When you sign up for LOGBOOK, you will automatically be subscribed to our occasional newsletter, which keeps you informed about the latest news from LOGBOOK. You can unsubscribe from this newsletter at any time.

        With best regards,
        **The LOGBOOK team**
    passwordReset:
      subject: "LOGBOOK - Forgot password"
      footer: |-
        This email was sent because you submitted a forgotten password request in the LOGBOOK login dialog.
        If you did not submit a request, you can ignore this email.
      body: |-
        Hello {name},

        Forgot your password?

        No problem! Click the button below to start the password reset process.

        [Reset Password]({reset_link} "button")

        You can then set a new password in the login dialog.

        If you do remember your password, you can ignore this email and try to log in again.

        Best regards,
        **The LOGBOOK Team**
    betaInvite:
      subject: "Welcome to beta test of LOGBOOK"
      footer: |-
        You are receiving this email because you signed up as a beta tester for LOGBOOK.
      body: |-
        Hello {name},

        Welcome to the **LOGBOOK** beta test! Thank you for taking the time to test our software under real conditions. Your feedback helps us prepare LOGBOOK for productive use on board.

        At the beginning of the beta test, LOGBOOK is available for Windows, macOS and iOS (iPad). Soon we will also include Android in the beta test program.

        **What is the role of a beta tester?**

        As a beta tester, you get access to a version that is already functional but may still contain errors. Your tasks include:

        -   Testing the features in real operation
        -   Reporting errors (bugs)
        -   Feedback on usability, comprehensibility, and user-friendliness
        -   Suggestions for improvements and new features
        -   Feedback on whether the software meets your expectations

        Post your error reports, comments, and ideas in our beta testing forum, which we have set up on Discord. With this link, you can access both the forum and the chat: [**Join the LOGBOOK community**](https://discord.gg/nTztPF9jKE). (On the iPad, the link works with the Discord app, not with the browser.)

        You can also reach us by email at contact@trimutic.com.

        **We will post important information and tips in the forum to help you test and use LOGBOOK.
        So please be sure to register!**

        We appreciate all feedback — brief or detailed, positive or critical. The more honest, the better.

        **How does the beta test work?**

        Use LOGBOOK directly on your next trip and report your experiences to us at contact@trimutic.com or through the contact form. Feel free to include screenshots.

        During the beta test phase, we will continue to improve LOGBOOK, incorporate feedback from our beta testers, and add some new features.
        Therefore, we will provide **updates** more frequently.
        You will automatically receive information about whether an update is available when starting the app or through the **Changelog** menu item in the **Help** menu (internet connection required in both cases).

        We would like to ask you to install updates promptly. This is the only way to ensure that you are really testing with the latest version.

        **What do you need to do next?**

        1.  Sign up as a beta tester for LOGBOOK.

        [Beta Tester Sign-up](https://logbook.yachts/app/signup/beta "button")

        If you want to register with LOGBOOK using **Safari**, you must in ensure with this first version that no fields are filled in automatically.
        So, please type in your name and email address manually; do not select any suggestions. Your entries will still be saved in your system.
        Or use a different browser for registration, such as Google Chrome or Microsoft Edge.

        2. Download and install the LOGBOOK app on your primary device, i.e., the device with which you want to receive the NMEA data.

            [Download App](https://logbook.yachts/site/en/download "button")

            If you signed up for the test on the **iPad**, you will receive an email from TestFlight in addition to this email.

            **TestFlight** is Apple's tool for running and testing unreleased apps on the iPad. Install the **TestFlight** app ([Install TestFlight](https://testflight.apple.com/a)). You can then load LOGBOOK into TestFlight using the **View in TestFlight** button (in the email) and install it on your iPad.

            After that, LOGBOOK can be launched like any other app.

        3. Start the app and log in to the user account you created in step 1.
        4. Create your first ship, connect the app to NMEA, and start your logbook (see also our [Quickstart](https://logbook.yachts/site/en/docs/quickstart/)).
        5. Discover all features and possibilities – and test the app under real conditions.

        **Your Contact to LOGBOOK Support**

        You can reach our support within the app through the **Contact Support** function in the **Help** menu (contact form) or by **email** at contact@trimutic.com. We will help you quickly and easily.

        Our regular support hours are Monday to Friday from 9:00 AM to 6:00 PM (CET). In many cases, you can also reach us outside these hours and on weekends.

        **Documentation**

        You can find all important information and instructions in our comprehensive [online documentation](https://logbook.yachts/site/en/docs/intro).

        For a quick start, we recommend our [Quickstart]( https://logbook.yachts/site/en/docs/quickstart/).

        Fair winds and always a hand's breadth of water under the keel!
        **From your LOGBOOK Team**
    subscriptionWelcomeSkipper:
      subject: "Your “LOGBOOK Skipper” subscription has been activated."
      footer: |-
        You are receiving this email because you registered with LOGBOOK.
      body: |-
        Hello {name},

        Congratulations and welcome aboard!

        You have successfully subscribed to **LOGBOOK Skipper**. We are delighted to welcome you to our community and wish you much enjoyment and success with your digital logbook.

        **What should you do next?**

        1.  Install the LOGBOOK app on your primary device, i.e., the device with which you want to receive NMEA data. You can find download links [here](https://logbook.yachts/site/en/download).
        2.  Start the app and log in to your user account.
        3.  Create your first vessel, connect the app to the NMEA, and start your logbook (see also our [Quickstart](https://logbook.yachts/site/en/docs/quickstart/)).
        4.  Discover all features and possibilities – if you have any questions, our support team will be happy to help.

        And if you've already tested LOGBOOK, you can simply continue with the logbook you started as a test.

        **Your Support Access:**

        With the **LOGBOOK Skipper** subscription, you can reach our support within the app via the **Contact function** in the Help menu or by **email** at contact@trimutic.com. We'll help you quickly and easily.

        Our regular support hours are Monday to Friday from 9:00 AM to 6:00 PM (CET). In many cases, you can also reach us outside these hours and on weekends.

        **Documentation:**

        You can find all important information and instructions in our comprehensive [online documentation](https://logbook.yachts/site/en/docs/intro).

        For a quick start, we recommend our [Quickstart](https://logbook.yachts/site/en/docs/quickstart/).

        Fair winds and following seas!
        **Your LOGBOOK Team**
    subscriptionWelcomeCaptain:
      subject: "Your “LOGBOOK Captain” subscription has been activated."
      footer: |-
        You are receiving this email because you registered with LOGBOOK.
      body: |-
        Hello {name},

        Congratulations and welcome aboard!

        You have successfully subscribed to **LOGBOOK Captain**. We are delighted to welcome you to our community and wish you much enjoyment and success with your digital logbook.

        **What should you do next?**

        1.  Install the LOGBOOK app on your primary device, i.e., the device with which you want to receive NMEA data. You can find download links [here](https://logbook.yachts/site/en/download).
        2.  Start the app and log in to your user account.
        3.  Create your first vessel, connect the app to the NMEA, and start your logbook (see also our [Quickstart](https://logbook.yachts/site/en/docs/quickstart/)).
        4.  Discover all features and possibilities – if you have any questions, our support team will be happy to help.

        And if you've already tested LOGBOOK, you can simply continue with the logbook you started as a test.

        **Your Support Access:**

        With the **LOGBOOK Captain** subscription, you can reach our support directly from the app via the **Contact Support** menu item in the Help menu or by **email** at [contact@trimutic.com](mailto:contact@trimutic.com). We'll help you quickly and easily.

        With the **Pro Support** included in your subscription, you also have the option to contact our support by phone or via WhatsApp. The exclusive phone number for Pro Support is **[+49 211 93659134](tel:4921193659134)**.

        Use this [**link**](https://wa.me/message/MNUAAKYACQU5A1) to connect with our Pro WhatsApp support channel. As a Captain subscriber, we offer you the opportunity to contact our support team via this channel through WhatsApp:

        Our regular support hours are Monday to Friday from 9:00 AM to 6:00 PM (CET). In many cases, you can also reach us outside these hours and on weekends.

        **Documentation:**

        You can find all important information and instructions in our comprehensive [online documentation](https://logbook.yachts/site/en/docs/intro).

        For a quick start, we recommend our [Quickstart](https://logbook.yachts/site/en/docs/quickstart/).

        Fair winds and following seas!
        **Your LOGBOOK Team**
    subscriptionWelcomeMasterCaptain:
      subject: "Your “LOGBOOK Master Captain” subscription has been activated."
      footer: |-
        You are receiving this email because you registered with LOGBOOK.
      body: |-
        Hello {name},

        Congratulations and welcome aboard!

        You have successfully subscribed to **LOGBOOK Master Captain**. We are delighted to welcome you to our community and wish you much enjoyment and success with your digital logbook.

        **What should you do next?**

        1.  Install the LOGBOOK app on your primary device, i.e., the device with which you want to receive NMEA data. You can find download links [here](https://logbook.yachts/site/en/download).
        2.  Start the app and log in to your user account.
        3.  Create your first vessel, connect the app to the NMEA, and start your logbook (see also our [Quickstart](https://logbook.yachts/site/en/docs/quickstart/)).
        4.  Discover all features and possibilities – if you have any questions, our support team will be happy to help.

        And if you've already tested LOGBOOK, you can simply continue with the logbook you started as a test.

        **Your Support Access:**

        With the **LOGBOOK Master Captain** subscription, you can reach our support directly from the app via the **Contact Support** menu item in the Help menu or by **email** at [contact@trimutic.com](mailto:contact@trimutic.com). We'll help you quickly and easily.

        With the **Pro Support** included in your subscription, you also have the option to contact our support by phone or via WhatsApp. The exclusive phone number for Pro Support is **[+49 211 93659134](tel:4921193659134)**.

        Use this [**link**](https://wa.me/message/MNUAAKYACQU5A1) to connect with our Pro WhatsApp support channel. As a Master Captain subscriber, we offer you the opportunity to contact our support team via this channel through WhatsApp:

        Please let us know, when you will need our onboarding support.

        Our regular support hours are Monday to Friday from 9:00 AM to 6:00 PM (CET). In many cases, you can also reach us outside these hours and on weekends.

        **Documentation:**

        You can find all important information and instructions in our comprehensive [online documentation](https://logbook.yachts/site/en/docs/intro).

        For a quick start, we recommend our [Quickstart](https://logbook.yachts/site/en/docs/quickstart/).

        Fair winds and following seas!
        **Your LOGBOOK Team**
    trialStarted:
      subject: "Your LOGBOOK Trial has started"
      footer: |-
        You're receiving this email because you signed up for LOGBOOK.
      body: |-
        Hello {name},

        Congratulations and welcome aboard!

        You have just started the 5-day demo phase of LOGBOOK. You can now create logbook entries for free on a total of **5 days**.

        -   **Find support to get started:**

        -   Step-by-step guide in the [Quickstart](https://logbook.yachts/site/en/docs/quickstart/)
        -   Explore playfully with our [interactive demos](https://logbook.yachts/site/en/demos/)
        -   Complete overview in the [Documentation](https://logbook.yachts/site/en/docs)

        -   **Tips & Help:**

        -   Practical tricks on our [Support page](https://logbook.yachts/site/en/docs/support/#tips).
        -   Direct help via the **Help** menu in the app or by email at contact@trimutic.com – we'll respond as quickly as possible!
        ---
        **Do you like LOGBOOK?**
        Then secure a subscription now and keep your logbook permanently and without interruption.

        [More info about subscriptions](https://logbook.yachts/site/en/prices "button")

        [Subscribe now](https://logbook.yachts/app/account/subscription "button")

        **Note:** Unfortunately, on iOS it's not possible to select the subscription directly from your account settings in the app. On the iPad, you need to log into your account in a web browser (e.g., Safari) – outside the app. The **Subscribe now** button will then take you to the subscription selection.

        If you have any questions or problems, our [Support Team](https://logbook.yachts/site/en/support) is happy to help.

        Best regards,
        **The LOGBOOK Team**
    trialExpiring:
      subject: "Your LOGBOOK Trial is ending soon"
      footer: |-
        You're receiving this email because you signed up for LOGBOOK.
      body: |-
        Hello {name},

        There is only one more day left until the 5-day LOGBOOK trial phase will end.

        **Do you like LOGBOOK?**
        Then secure a subscription now and keep your logbook permanently and without interruption.

        [More info about subscriptions](https://logbook.yachts/site/en/prices "button")

        [Subscribe now](https://logbook.yachts/app/account/subscription "button")

        **Note:** Unfortunately, on iOS it's not possible to select the subscription directly from your account settings in the app. On the iPad, you need to log into your account in a web browser (e.g., Safari) – outside the app. The **Subscribe now** button will then take you to the subscription selection.

        If you have any questions or problems, our [Support Team](https://logbook.yachts/site/en/support) is happy to help.

        Best regards,
        **The LOGBOOK Team**
    trialExpired:
      subject: "Your LOGBOOK Trial phase has expired"
      footer: |-
        You're receiving this email because you signed up for LOGBOOK.
      body: |-
        Hello {name},

        Your LOGBOOK trial phase has expired. We will keep your test data in the LOGBOOK cloud for 6 more months.

        **Do you like LOGBOOK?**
        Then secure a subscription now and keep your logbook permanently and without interruption.

        [More info about subscriptions](https://logbook.yachts/site/en/prices "button")

        [Subscribe now](https://logbook.yachts/app/account/subscription "button")

        **Note:** Unfortunately, on iOS it's not possible to select the subscription directly from your account settings in the app. On the iPad, you need to log into your account in a web browser (e.g., Safari) – outside the app. The **Subscribe now** button will then take you to the subscription selection.

        If you have any questions or problems, our [Support Team](https://logbook.yachts/site/en/support) is happy to help.

        Best regards,
        **The LOGBOOK Team**
    contact:
      subject: "LOGBOOK - We Received Your Message"
      footer: |-
        This email was sent because you submitted a contact form on our website.
        If you did not submit this form, please contact us immediately.
      body: |-
        Hello {name},

        We've received your message and our customer service team will get back to you as soon as possible.

        ### Your Inquiry Details

        **Email:** {email}

        **Type of Inquiry:** {topic}

        **Your Question:**

        {request}

        This email confirms we've received your message. We typically respond within 1-2 business days.

        If you have any urgent questions in the meantime, feel free to reach out to us by reply to this email.

        Best regards,
        **The LOGBOOK Customer Service Team**
    betaContact:
      subject: "LOGBOOK Beta Testing - Thank You"
      footer: |-
        This email was sent because you signed up for the LOGBOOK beta testing program.
        If you did not sign up, please disregard this email.
      body: |-
        Hello {name},

        Thank you for your interest in helping us test LOGBOOK!
        We're excited to have you on board as one of our beta testers.

        ### Your Registration Details

        **Email:** {email}

        **Operating System:** {system}

        **Current Logbook Suite User:** {logbooksuite}

        **Details about your vessel, NMEA connectivity, and your planned trips for early 2026:**
        {request}

        ### What Happens Next?

        1.  **Review Process:** Our team will carefully review your application and vessel details.
        2.  **Information:** We'll contact you with more details of the beta program.
        3.  **Beta Access:** As tester you will receive download links and setup instructions.
        4.  **Testing Period:** You'll have the opportunity to test LOGBOOK during your planned trips in early 2026.

        We're particularly interested in testing LOGBOOK with various vessel configurations and NMEA connectivity setups, so your input will be invaluable in making LOGBOOK the best digital logbook solution available.

        If you have any questions in the meantime, please don't hesitate to reach out to us.

        Fair winds and following seas,
        **The LOGBOOK Team**
